import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Users, FileText, CreditCard, PlusCircle } from 'lucide-react';
import { useMerchantDashboard } from '@/hooks-v2/aggregator';
import { useCurrentMerchant } from '@/hooks-v2/context';
import { StatsCard } from '../../components/merchant/StatsCard';
import { useTranslation } from '@/i18n';
import { toast } from 'react-hot-toast';

// Stats Card Skeleton component for loading state
const StatsCardSkeleton: React.FC = () => (
	<div className='bg-white rounded-xl shadow-sm border p-6 animate-pulse'>
		<div className='flex items-center justify-between'>
			<div className='flex-1'>
				<div className='h-4 bg-gray-200 rounded w-24 mb-3'></div>
				<div className='h-8 bg-gray-300 rounded w-16'></div>
			</div>
			<div className='w-16 h-16 bg-gray-200 rounded-full'></div>
		</div>
	</div>
);

const Dashboard: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	// Note: user_role is 'user' or 'super_admin'; merchant role is in user_merchants table
	// On web, all authenticated users are merchants; server handles actual authorization

	const {
		selectedMerchant: merchant,
		selectedMerchantId,
		loading: isLoadingMerchants,
		error: merchantsError,
	} = useCurrentMerchant();

	const dashboard = useMerchantDashboard(selectedMerchantId);
	const isLoading = isLoadingMerchants || dashboard.isLoading;

	// Show toast on error
	useEffect(() => {
		if (dashboard.error) {
			toast.error(dashboard.error);
		}
	}, [dashboard.error]);

	useEffect(() => {
		if (merchantsError) {
			toast.error(merchantsError);
		}
	}, [merchantsError]);

	// Loading state - show skeleton loaders
	if (isLoading) {
		return (
			<div className='flex-1 overflow-auto p-6'>
				{/* Header Skeleton */}
				<div className='mb-6'>
					<div className='animate-pulse'>
						<div className='h-8 bg-gray-200 rounded w-48 mb-2'></div>
						<div className='h-4 bg-gray-100 rounded w-32'></div>
					</div>
				</div>

				{/* Stats Grid Skeleton */}
				<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8'>
					<StatsCardSkeleton />
					<StatsCardSkeleton />
					<StatsCardSkeleton />
				</div>

				{/* Quick Actions Skeleton */}
				<div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8'>
					{[1, 2, 3, 4].map(i => (
						<div key={i} className='bg-white border border-gray-200 rounded-xl p-6 animate-pulse'>
							<div className='flex items-center'>
								<div className='w-12 h-12 bg-gray-200 rounded-lg'></div>
								<div className='ml-4 flex-1'>
									<div className='h-5 bg-gray-200 rounded w-24 mb-2'></div>
									<div className='h-4 bg-gray-100 rounded w-32'></div>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		);
	}

	// Compute number of quick actions for responsive grid (4 actions for all merchants)
	const quickActionGridCols = 'lg:grid-cols-4';

	return (
		<div className='flex-1 overflow-auto p-6'>
			{/* Header */}
			<div className='mb-6'>
				<div className='flex items-center justify-between'>
					<div>
						<h1 className='text-2xl font-bold text-gray-900'>
							{merchant?.merchantName
								? t('merchant.dashboard.welcomeBack', { merchantName: merchant.merchantName })
								: t('merchant.dashboard.title')}
						</h1>
						<p className='text-gray-500'>{t('merchant.dashboard.overview')}</p>
					</div>
				</div>
			</div>

			{/* Stats Grid */}
			<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8'>
				{/* Total Redemptions */}
				<StatsCard
					title={t('merchant.dashboard.stats.totalRedemptions')}
					value={dashboard.error || !dashboard.computed.hasDashboardData ? '—' : dashboard.computed.totalRedemptions}
					icon={CreditCard}
					color='blue'
					onClick={() => {
						void navigate('/merchant/redemption#history');
					}}
				/>

				{/* Active Coupons */}
				<StatsCard
					title={t('merchant.dashboard.stats.activeCoupons')}
					value={dashboard.error || !dashboard.computed.hasDashboardData ? '—' : dashboard.computed.activeCoupons}
					icon={Gift}
					color='green'
					onClick={() => {
						void navigate('/merchant/coupon-list');
					}}
				/>

				{/* Team Members */}
				<StatsCard
					title={t('merchant.dashboard.stats.teamMembers')}
					value={dashboard.error || !dashboard.computed.hasDashboardData ? '—' : dashboard.computed.totalMembers}
					icon={Users}
					color='purple'
					onClick={() => {
						void navigate('/merchant/member-list');
					}}
				/>
			</div>

			{/* Quick Actions */}
			<div className={`grid grid-cols-1 md:grid-cols-2 ${quickActionGridCols} gap-6 mb-8`}>
				{/* Redeem Coupon */}
				<button
					onClick={() => {
						void navigate('/merchant/redemption');
					}}
					className='bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow group text-left'
				>
					<div className='flex items-center'>
						<div className='bg-blue-100 p-3 rounded-lg group-hover:bg-blue-200 transition-colors'>
							<CreditCard className='h-6 w-6 text-blue-600' />
						</div>
						<div className='ml-4 text-left'>
							<h3 className='text-lg font-semibold text-gray-900'>
								{t('merchant.dashboard.quickActions.redeemCoupon')}
							</h3>
							<p className='text-gray-500'>{t('merchant.dashboard.quickActions.redeemCouponDesc')}</p>
						</div>
					</div>
				</button>

				{/* Create Coupon */}
				<button
					onClick={() => {
						void navigate('/merchant/coupon-list/new');
					}}
					className='bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow group text-left'
				>
					<div className='flex items-center'>
						<div className='bg-orange-100 p-3 rounded-lg group-hover:bg-orange-200 transition-colors'>
							<PlusCircle className='h-6 w-6 text-orange-600' />
						</div>
						<div className='ml-4 text-left'>
							<h3 className='text-lg font-semibold text-gray-900'>
								{t('merchant.dashboard.quickActions.createCoupon')}
							</h3>
							<p className='text-gray-500'>{t('merchant.dashboard.quickActions.createCouponDesc')}</p>
						</div>
					</div>
				</button>

				{/* Manage Coupons */}
				<button
					onClick={() => {
						void navigate('/merchant/coupon-list');
					}}
					className='bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow group text-left'
				>
					<div className='flex items-center'>
						<div className='bg-green-100 p-3 rounded-lg group-hover:bg-green-200 transition-colors'>
							<FileText className='h-6 w-6 text-green-600' />
						</div>
						<div className='ml-4 text-left'>
							<h3 className='text-lg font-semibold text-gray-900'>
								{t('merchant.dashboard.quickActions.manageCoupons')}
							</h3>
							<p className='text-gray-500'>{t('merchant.dashboard.quickActions.manageCouponsDesc')}</p>
						</div>
					</div>
				</button>

				{/* View Members */}
				<button
					onClick={() => {
						void navigate('/merchant/member-list');
					}}
					className='bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow group text-left'
				>
					<div className='flex items-center'>
						<div className='bg-purple-100 p-3 rounded-lg group-hover:bg-purple-200 transition-colors'>
							<Users className='h-6 w-6 text-purple-600' />
						</div>
						<div className='ml-4 text-left'>
							<h3 className='text-lg font-semibold text-gray-900'>
								{t('merchant.dashboard.quickActions.teamMembers')}
							</h3>
							<p className='text-gray-500'>{t('merchant.dashboard.quickActions.teamMembersDesc')}</p>
						</div>
					</div>
				</button>
			</div>
		</div>
	);
};

export default Dashboard;
